import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { Container } from '@mui/material';
import { DragDropContext } from 'react-beautiful-dnd';

// Components
import Header from './components/Header';
import ProtectedRoute from './components/ProtectedRoute';
import CinematicLoader from './components/common/CinematicLoader';

// Context
import { DragProvider, useDrag } from './contexts/DragContext';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import TestsPage from './pages/TestsPage';
import TestDetailPage from './pages/TestDetailPage';
import CreateTestPage from './pages/CreateTestPage';
import InterviewPage from './pages/InterviewPage';
import InterviewResultsPage from './pages/InterviewResultsPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import UnlockApprovePage from './pages/UnlockApprovePage';
import UnlockSuccessPage from './pages/UnlockSuccessPage';

// Auth Context
import { useAuth } from './contexts/AuthContext';

// Inner component that uses the drag context
function AppRoutes() {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const { handleDrag } = useDrag();

  if (isLoading && location.pathname !== '/auth/callback') {
    return <CinematicLoader message="Authenticating" submessage="Verifying your credentials and preparing your dashboard" />;
  }

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <DragDropContext onDragEnd={handleDrag}>
          <Routes>
            <Route path="/" element={!isAuthenticated ? <LoginPage /> : <Navigate to="/dashboard" />} />
            <Route path="/login" element={!isAuthenticated ? <LoginPage /> : <Navigate to="/dashboard" />} />
            <Route path="/register" element={!isAuthenticated ? <RegisterPage /> : <Navigate to="/dashboard" />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Navigate to="/tests" />
              </ProtectedRoute>
            } />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            
            <Route path="/tests" element={
              <ProtectedRoute>
                <TestsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/tests/new" element={
              <ProtectedRoute>
                <CreateTestPage />
              </ProtectedRoute>
            } />
            
            <Route path="/tests/:id" element={
              <ProtectedRoute>
                <TestDetailPage />
              </ProtectedRoute>
            } />
            
            <Route path="/interview/:id" element={<InterviewPage />} />
            
            
            <Route path="/interviews/:uuid/results" element={
              <ProtectedRoute>
                <InterviewResultsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/dashboard/unlock-approve" element={
              <ProtectedRoute>
                <UnlockApprovePage />
              </ProtectedRoute>
            } />
            
            <Route path="/manager/unlock-success" element={
              <ProtectedRoute>
                <UnlockSuccessPage />
              </ProtectedRoute>
            } />
            
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </DragDropContext>
      </Container>
    </>
  );
}

function App() {
  const location = useLocation();
  const { isAuthenticated, isLoading, login, logout } = useAuth();

  useEffect(() => {
    // Skip authentication check on callback page
    if (location.pathname === '/auth/callback') {
      return;
    }

    // Only check auth state once on app load, not on every navigation
    if (isAuthenticated) {
      return;
    }

    const checkAuthState = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        
        // Store the access token in localStorage for axios
        const session = await Auth.currentSession();
        const accessToken = session.getAccessToken().getJwtToken();
        localStorage.setItem('aws-token', accessToken);
        
        // Let AuthContext handle user data extraction and merging
        // Don't duplicate the logic here to avoid overwriting complete data
      } catch (error) {
        // Clear token if authentication fails
        localStorage.removeItem('aws-token');
        logout();
      }
    };

    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      logout();
    }, 5000);

    checkAuthState().finally(() => {
      clearTimeout(timeoutId);
    });
  }, []); // Remove location.pathname dependency to prevent re-auth on navigation

  return (
    <DragProvider>
      <AppRoutes />
    </DragProvider>
  );
}

export default App;
